import { useEffect, useMemo, useState } from 'react';

import { useClientTheme } from '@/hooks/client/useClientTheme';
import { useExploraLoader } from '@/lib/exploraLoader';

import { LOADER_STEPS, loadCasaLineas } from './exploraLoaderSteps';
import './exploraLoader.css';

type Lines = Awaited<ReturnType<typeof loadCasaLineas>>;

const STEP_MS = 1400;
/** Nothing shows for a fast (cached) load: the figure and caption fade in only after this. */
const SHOW_AFTER_MS = 250;
// The pen goes over the house, then the vegetation: about 2 s in all, the Mi boda draw-in shortened for a loader
const HOUSE_MS = 1000;
const VEG_MS = 500;

/**
 * Covers Explora from the moment our code runs to the map's first frame (lib/exploraLoader drives it): the Mi boda house,
 * in its line work alone (its own small chunk), draws in while the page and the map download, then breathes (opacity
 * only, so it keeps moving while building the scene blocks the main thread) and fades out into the flythrough. Mounted
 * once at the app's root, above the routes' own Suspense boundaries, so it never restarts on the way.
 */
export function ExploraLoader() {
  const { active, loaded, leaving } = useExploraLoader();
  if (!active) return null;
  return <LoaderScreen loaded={loaded} leaving={leaving} />;
}

function LoaderScreen({ loaded, leaving }: { loaded: boolean; leaving: boolean }) {
  const { resolved } = useClientTheme();
  const [lines, setLines] = useState<Lines | null>(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    let live = true;
    loadCasaLineas().then(
      (m) => live && setLines(m),
      () => {}
    );
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (leaving) return;
    const t = window.setInterval(
      () => setStep((s) => Math.min(s + 1, LOADER_STEPS.length - 1)),
      STEP_MS
    );
    return () => window.clearInterval(t);
  }, [leaving]);

  const paths = useMemo(() => {
    if (!lines) return null;
    const marks = lines.CASA_LINEAS;
    const houseCount = marks.filter(([, , f]) => !(f & 1)).length || 1;
    const vegCount = marks.length - houseCount || 1;
    let h = 0;
    let v = 0;
    return marks.map(([d, w, flags], i) => {
      const delay =
        flags & 1 ? HOUSE_MS + (v++ / vegCount) * VEG_MS : (h++ / houseCount) * HOUSE_MS;
      return (
        <path
          key={i}
          d={d}
          strokeWidth={w}
          pathLength={1}
          className={flags & 2 ? 'ml-soft' : undefined}
          style={{ animationDelay: `${Math.round(SHOW_AFTER_MS + delay)}ms` }}
        />
      );
    });
  }, [lines]);

  return (
    <div
      role="status"
      aria-label="Cargando Casa Aragón"
      data-testid="explora-loader"
      data-theme={resolved}
      data-surface="scene"
      data-phase={loaded ? 'build' : 'download'}
      data-leaving={leaving || undefined}
      className="client-app explora-loader fixed inset-0 z-[100] flex flex-col items-center justify-center gap-6 bg-ca-media-base px-4 font-[family-name:var(--ca-font)] font-ca-light data-[leaving]:pointer-events-none"
    >
      <div className="ml-figure w-full max-w-md md:max-w-xl">
        {lines ? (
          <svg
            viewBox={lines.VIEW_BOX_LINEAS}
            aria-hidden="true"
            className="ml-lines block h-auto w-full text-ca-accent"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {paths}
          </svg>
        ) : null}
      </div>
      <p className="ml-caption m-0 text-ca-body text-ca-muted">
        <span key={step} className="ml-step">
          {LOADER_STEPS[step]}
        </span>
      </p>
    </div>
  );
}
